import { useEffect, useRef, useState } from "react";
import { api, errMsg, GEAR_CATEGORIES } from "../lib/api";
import { AdminHead } from "./AdminLayout";

const STUDIO_TABS = [
  { key: "balboa", label: "Balboa" },
  { key: "bungalow", label: "The Bungalow" },
];

const blank = { category: GEAR_CATEGORIES[0], name: "", model: "" };

export default function GearAdmin() {
  const [studio, setStudio] = useState("balboa");
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(blank);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");
  const dragId = useRef(null);
  const currentStudio = useRef(studio);

  const load = (s = studio) =>
    api
      .get(`/gear?studio=${s}`)
      .then((r) => {
        if (currentStudio.current !== s) return; // stale response, another room is selected
        setItems(r.data?.items || []);
      })
      .catch((e) => setErr(errMsg(e)));

  useEffect(() => {
    currentStudio.current = studio;
    setItems([]);
    setEditing(null);
    setForm(blank);
    load(studio);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studio]);

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const payload = { ...form, studio };
      if (editing) await api.put(`/admin/gear/${editing}`, payload);
      else await api.post("/admin/gear", payload);
      setForm(blank);
      setEditing(null);
      load();
    } catch (e2) {
      setErr(errMsg(e2));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this gear item?")) return;
    await api.delete(`/admin/gear/${id}`);
    load();
  };

  const onDrop = async (cat, targetId) => {
    const list = items.filter((i) => i.category === cat);
    const from = list.findIndex((i) => i.id === dragId.current);
    const to = list.findIndex((i) => i.id === targetId);
    if (from < 0 || to < 0 || from === to) return;
    const next = [...list];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    await api.put("/admin/gear/reorder", { ids: next.map((i) => i.id) });
    load();
  };

  return (
    <div data-testid="admin-gear">
      <AdminHead
        title="Gear"
        sub={`${items.length} items in this room · drag to reorder within a category`}
      />

      <div className="studio-switch" style={{ marginBottom: 32 }} data-testid="admin-gear-switch">
        {STUDIO_TABS.map((t) => (
          <button
            key={t.key}
            className={`tab studio-tab${studio === t.key ? " active" : ""}`}
            onClick={() => setStudio(t.key)}
            data-testid={`admin-gear-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <form className="card" style={{ marginBottom: 40 }} onSubmit={save} data-testid="gear-form">
        <p className="label" style={{ marginBottom: 12 }}>
          {editing ? "Edit item" : "Add item"} — {STUDIO_TABS.find((t) => t.key === studio).label}
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 16 }}>
          <label className="field" style={{ marginBottom: 0 }}>
            <span>Category</span>
            <select
              className="inp"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              data-testid="gear-category"
            >
              {GEAR_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <label className="field" style={{ marginBottom: 0 }}>
            <span>Name</span>
            <input
              className="inp"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="gear-name"
            />
          </label>
          <label className="field" style={{ marginBottom: 0 }}>
            <span>Model / detail</span>
            <input
              className="inp"
              value={form.model}
              onChange={(e) => setForm({ ...form, model: e.target.value })}
              data-testid="gear-model"
            />
          </label>
        </div>
        {err && <p className="mono" style={{ color: "var(--orange)", fontSize: 13, marginTop: 12 }}>{err}</p>}
        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <button className="btn btn-sm" type="submit" data-testid="gear-save">
            {editing ? "Save" : "Add"}
          </button>
          {editing && (
            <button
              className="btn btn-sm btn-ghost"
              type="button"
              onClick={() => {
                setEditing(null);
                setForm(blank);
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {GEAR_CATEGORIES.map((cat) => {
        const list = items.filter((i) => i.category === cat);
        return (
          <div key={cat} style={{ marginBottom: 40 }}>
            <h2 className="disp" style={{ fontSize: 20 }}>{cat}</h2>
            <div className="misreg-rule" style={{ marginTop: 8, marginBottom: 12 }} />
            {list.length ? (
              list.map((i) => (
                <div
                  key={i.id}
                  className="drag-item"
                  draggable
                  onDragStart={() => (dragId.current = i.id)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onDrop(cat, i.id)}
                  style={{
                    display: "flex",
                    gap: 12,
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: "1px solid var(--shadow)",
                    padding: "10px 0",
                  }}
                  data-testid={`gear-row-${i.id}`}
                >
                  <span className="mono" style={{ fontSize: 14 }}>
                    ⠿ {i.name} {i.model && <span style={{ opacity: 0.6 }}>— {i.model}</span>}
                  </span>
                  <span style={{ display: "flex", gap: 8 }}>
                    <button
                      className="btn btn-sm btn-ghost"
                      onClick={() => {
                        setEditing(i.id);
                        setForm({ category: i.category, name: i.name, model: i.model || "" });
                      }}
                      data-testid={`gear-edit-${i.id}`}
                    >
                      Edit
                    </button>
                    <button className="btn btn-sm" onClick={() => remove(i.id)} data-testid={`gear-delete-${i.id}`}>
                      Delete
                    </button>
                  </span>
                </div>
              ))
            ) : (
              <p className="mono" style={{ fontSize: 13, opacity: 0.6 }}>— nothing here yet —</p>
            )}
          </div>
        );
      })}
    </div>
  );
}
